import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonCard,
  IonChip,
  IonHeader,
  IonIcon,
  IonLabel,
  IonToolbar,
  IonSearchbar,
  IonTitle,
  IonRow,
  IonContent,
  IonList,
  useIonViewWillEnter,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import AllergiesSearchList from '../page/AllergiesSearchList/AllergiesSearchList.component'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { IAllergyOptions } from '../store/patientAllergies.types'
import { chevronBackOutline, closeCircleOutline } from 'ionicons/icons'
import { deletePatientAllergy } from '../store/patientAllergies.actions'
import { useDispatch, useSelector } from 'react-redux'
import { getPatientAllergiesSelector } from '../selectors'
import './PatientAllergiesSearchListContent.component.css'
import { setSelectedOption } from '../../patientInformation/store/patientInformation.actions'
import { PatientInformationConstants } from '../../patientInformation/store/patientInformation.constants'
import { getPatientAllergyOptionsSelector } from '../../options/selectors'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { IOption } from '../../../common/common.types'
import { initializeSearch } from '../../../components/search/store/search.actions'

const PatientAllergiesSearchListContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isTablet: isTabletDevice } = useDeviceMetrics()
  const patientAllergiesState = useSelector(getPatientAllergiesSelector)
  const { allergies } = patientAllergiesState
  const patientAllergiesOptions = useSelector(getPatientAllergyOptionsSelector)
  const { chunkedPatientAllergyOptions, patientAllergies } = patientAllergiesOptions
  const [searchValue, setSearchValue] = useState('')
  const [searchResult, setSearchResult] = useState(chunkedPatientAllergyOptions)
  const history = useHistory()

  useIonViewWillEnter(() => {
    setSearchValue('')
  })

  const deleteAllergy = (e: BaseSyntheticEvent, option: IAllergyOptions) => {
    e.stopPropagation()
    option.selected = false
    dispatch(deletePatientAllergy(option))
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.stopPropagation()
    if (isTabletDevice) {
      dispatch(initializeSearch([]))
      dispatch(setSelectedOption(PatientInformationConstants.PATIENT_ALLERGIES_OPTION))
      history.push(TabConstants.PATIENT_INFO_ROUTE)
    } else {
      history.push(TabConstants.PATIENT_ALLERGIES)
    }
  }

  const handleSearch = (e: CustomEvent) => {
    setSearchValue(e.detail.value)
    if (e.detail.value === '') {
      const initialOption = patientAllergies.slice(0, 40)
      setSearchResult(initialOption)
    } else {
      const tempSearchResult = patientAllergies.filter((allergy) =>
        allergy.name?.toLowerCase().includes(e.detail.value.toLowerCase().trim())
      )
      tempSearchResult.map((option: IAllergyOptions) => {
        const item: IAllergyOptions | undefined = allergies.find((item: IOption) => item.name === option.name)
        if (item) {
          option.selected = item.selected
        } else {
          option.selected = false
        }
        return option
      })
      setSearchResult(tempSearchResult)
    }
  }

  useEffect(() => {
    if (searchValue) {
      let content: HTMLIonContentElement | null = document.querySelector(':not(.ion-page-hidden) > ion-content')
      content?.scrollToTop(200)
      content = null
    }
  }, [searchValue])

  useEffect(() => {
    setTimeout(() => {
      let content: HTMLIonContentElement | null = document.querySelector('#allergies-list-row')
      content?.scrollBy(content?.scrollWidth, 0)
      content = null
    }, 10)
  }, [allergies])

  return (
    <>
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' color='primary-alt' onClick={onBackClick}>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle data-testid='HeaderTitle'>{t('patientAllergies.Allergies')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonToolbar className='toolbar-padding'>
        <IonLabel className='toolbar-label' slot='start'>
          {t('patientAllergies.PatientAllergies')}:
        </IonLabel>
      </IonToolbar>
      {allergies.length > 0 ? (
        <IonToolbar>
          <IonRow class='ion-nowrap no-flex-shrink' id='allergies-list-row'>
            {allergies.map((option: IAllergyOptions, index: number) => (
              <IonChip data-testid={`Chip_${option.name}`} key={index} className='ion-chip'>
                <IonLabel>{option.name}</IonLabel>
                <IonIcon color='white' icon={closeCircleOutline} onClick={(e) => deleteAllergy(e, option)} />
              </IonChip>
            ))}
          </IonRow>
        </IonToolbar>
      ) : (
        <IonRow>
          <IonToolbar color='light' className='labelHeader'>
            <IonLabel color='medium'>{t('patientAllergies.AllergiesLabel')}</IonLabel>
          </IonToolbar>
        </IonRow>
      )}
      <IonToolbar color='light' className='ion-padding-top'>
        <IonSearchbar
          data-testid='Searchbar'
          debounce={100}
          value={searchValue}
          onIonChange={handleSearch}
          autocomplete='off'></IonSearchbar>
      </IonToolbar>

      <IonContent color='light' fullscreen>
        <IonCard className={isTabletDevice ? 'allergy-toggle-list card-width' : 'card-margin'}>
          <IonList>
            <AllergiesSearchList
              searchValue={searchValue}
              allergyOptions={searchResult}
              setSearchResults={setSearchResult}
              setSearchValue={setSearchValue}
            />
          </IonList>
        </IonCard>
      </IonContent>
    </>
  )
}

export default PatientAllergiesSearchListContent
